/**
 * R4 批量出片编排:**一份文案 × N 个形象**(2026-08-08)。
 *
 * ═══ 为什么是前端编排而不是后端批量端点 ═══════════════════════════════════════
 * 后端没有批量口,也不该为这个加一个:批量出片在语义上就是「N 次单条出片」——
 * 计费、配额、并发闸、场景打标、蓝图取用登记全都是按**单条**结算的。走 N 次单条
 * `createTask`,这些逻辑一条都不用改口径;真加一个批量端点,反而要在后端把上面那一串
 * 全再实现一遍「批量版」,还得回答「批量里失败一条整批算不算成功」这种本来不存在的问题。
 *
 * ═══ 三条硬规矩(都是这仓踩出来的)═══════════════════════════════════════════
 * ① **串行**,不并发。分身线有租户级在途上限,一次并发甩 N 条会自己把自己顶到
 *    「并发已满」;而且服务端计费是逐条扣的,并发失败时「扣了几条」根本说不清。
 * ② ★**一条失败不否决其余**(这仓踩过四次:一个格子超长 422 掉整份产物,4900 积分白干)。
 *    第 3 条失败,第 4、5 条照常提交、照常出片;失败那条只报它自己。
 * ③ ★但**余额/配额不足要提前收手**。这跟②不矛盾:②说的是「别让一条的问题牵连别人」,
 *    而余额耗尽是**共享前提没了** —— 后面每一条都必然失败,继续提交只是刷一串
 *    一模一样的红字。收手时必须说清「已成功 N 条、剩余 M 条没提交」,不含糊。
 *
 * ═══ 为什么不预先查余额 ═══════════════════════════════════════════════════
 * 方案单原话是「提交前查余额并说清够出几条」。实查:**租户侧根本没有余额接口** ——
 * `/me/credits`、`/me/balance`、`/billing/balance`、`/tenant/credits`、`/credits/balance`
 * 在测试机全是 404;唯一带余额的是 `/advisor/cockpit-summary`,但它是 TENANT_ADMIN 专属、
 * 还挂在军师模块闸后面,普通员工调是 403。拿它当余额源 = 一半用户看不到数,
 * 而且是拿一个分析大接口当计价器用。
 * 所以这里的口径是:**提交前把总价说满**(N 条 × 每条预计 = 总预计,都带「预计」二字),
 * 「够出几条」交给**真闸**回答 —— 余额不足时后端自己会拒,我们据此收手并报出真实条数。
 * 这比拿一个猜来的余额算「够出几条」诚实(存量教训:推断式契约三个生产者只有一个遵守)。
 */
import { ApiError } from "@/shared/api";

import type { DhTaskCreateRequest } from "../api/digitalHumanService";
import { errorText } from "@/shared/lib/apiErrors";

/** 批量里的一个目标形象。`avatarFields` 是这一条与其他条**唯一**的差别。 */
export interface DhBatchTarget {
  /** 稳定键(preset id / profile id),做 React key 与结果对位。 */
  key: string;
  label: string;
  thumbnailUrl?: string | null;
  /**
   * video 档形象的 thumbnailUrl 是**源视频**的 presign 地址(后端 DTO 口径)——
   * 弹框必须按它分支用 <video> 取首帧;丢了 kind 就是 <img> 硬塞 mp4,预览全裂(0901)。
   */
  kind?: "photo" | "video";
  avatarFields: Pick<DhTaskCreateRequest, "avatarSource" | "avatarRef" | "consentAcknowledged">;
}

export type DhBatchItemState = "queued" | "submitting" | "done" | "failed" | "skipped";

export interface DhBatchItem {
  target: DhBatchTarget;
  state: DhBatchItemState;
  taskId?: number;
  error?: string;
}

/** 余额/配额类错误码:后端拒的是**共享前提**,不是这一条的毛病 → 收手。 */
const BLOCKING_CODES = new Set(["BILLING_TICKET_INSUFFICIENT", "QUOTA_EXCEEDED"]);

/**
 * 有些老路径不抛 errorCode 只抛中文 message,再兜一层文本判断。
 * ★只兜**余额/配额**这一类;别的错一律按「单条失败」处理,不许扩大收手范围 ——
 *   收手会牵连后面没提交的那些,判错的代价比多报一条失败大。
 */
export function isBlockingError(e: unknown): boolean {
  if (e instanceof ApiError && BLOCKING_CODES.has(e.errorCode)) return true;
  const msg = e instanceof Error ? e.message : String(e ?? "");
  return /余额不足|积分不足|配额(超限|不足|已用完)/.test(msg);
}

export interface DhBatchOptions {
  targets: DhBatchTarget[];
  /** 除形象外完全相同的那份请求体(文案/音色/语速/档位/sceneKey/蓝图…)。 */
  base: Omit<DhTaskCreateRequest, "avatarSource" | "avatarRef" | "consentAcknowledged">;
  submit: (req: DhTaskCreateRequest) => Promise<{ taskId: number }>;
  /** 每条状态一变就回调,让 UI 能逐条亮起来(而不是转圈到最后一次性揭晓)。 */
  onProgress?: (items: DhBatchItem[]) => void;
  /** 用户中途点「停下」:已提交的不撤(钱已经花了),没提交的标 skipped。 */
  shouldAbort?: () => boolean;
}

export interface DhBatchResult {
  items: DhBatchItem[];
  succeeded: number;
  failed: number;
  skipped: number;
  /** 因余额/配额收手(而不是跑完)。 */
  stoppedForBalance: boolean;
  /** 用户主动停下。 */
  aborted: boolean;
}

/**
 * 串行跑完一批。**永远 resolve**,不 reject —— 批量的「结果」是一张逐条清单,
 * 不是一个成败布尔;抛异常会让调用方只能二选一地展示,恰恰丢掉「哪几条成了」。
 */
export async function runDhBatch(opts: DhBatchOptions): Promise<DhBatchResult> {
  const items: DhBatchItem[] = opts.targets.map((target) => ({ target, state: "queued" }));
  const emit = () => opts.onProgress?.(items.map((i) => ({ ...i })));
  emit();

  let stoppedForBalance = false;
  let aborted = false;

  for (let i = 0; i < items.length; i++) {
    if (stoppedForBalance || aborted || opts.shouldAbort?.()) {
      if (opts.shouldAbort?.() && !stoppedForBalance) aborted = true;
      items[i].state = "skipped";
      emit();
      continue;
    }
    items[i].state = "submitting";
    emit();
    try {
      const { taskId } = await opts.submit({ ...opts.base, ...items[i].target.avatarFields });
      items[i].state = "done";
      items[i].taskId = taskId;
    } catch (e) {
      items[i].state = "failed";
      items[i].error = errorText(e);
      // ★规矩②:普通失败**不**中断 —— 后面的照常提交。
      // ★规矩③:只有余额/配额这种「共享前提没了」才收手。
      if (isBlockingError(e)) stoppedForBalance = true;
    }
    emit();
  }

  return {
    items,
    succeeded: items.filter((i) => i.state === "done").length,
    failed: items.filter((i) => i.state === "failed").length,
    skipped: items.filter((i) => i.state === "skipped").length,
    stoppedForBalance,
    aborted,
  };
}
